import { coreApiClient, ApiError } from './core';
import {SessionDataDTO, SubmitAnswerRequest, SubmitAnswerResponse, SkillMastery} from '@/features/Vocab/vocab.ts';
import type {Chapter, SkillParams} from "@/products/greekbrain/features/Grammar/services/api.ts";

export interface PracticeReportPayload {
    exercise_id: string;
    exercise_type: string;
    reason: string;
    comment: string;
    report_mode: 'practice' | 'vocab' | 'geo' | 'picture';
}

class PracticeApi {
    /**
     * Universal answer submission endpoint
     * Replaces: handleReviewResponse, handleMatchingResult, handleChoiceResult
     */
    async submitAnswer(request: SubmitAnswerRequest): Promise<SubmitAnswerResponse> {
        try {
            const response = await coreApiClient.axios.post<SubmitAnswerResponse>(
                '/api/practice/submit-answer',
                request
            );
            return response.data;
        } catch (error) {
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Failed to submit answer'
            };
        }
    }


    /**
     * Fetch all chapters with skills
     * Returns vocabulary, grammar, and communication skills for each chapter
     */
    async getChapters(): Promise<{ data: Chapter[] }> {
        const response = await coreApiClient.axios.get(`/api/greekbrain/practice/chapters`);
        return response.data;
    }

    /**
     * Generate a new practice session
     * Throws ApiError with errorCode 'exercises_generating' if server returns 202 (exercises being generated)
     */
    async generatePractice(skill: SkillParams | null = null, focus: string = 'general', debug: boolean = false): Promise<SessionDataDTO> {
        const response = await coreApiClient.axios.post(`/api/greekbrain/practice/generate`, {
            skill_area: skill?.skill_area,
            skill_value: skill?.skill_value,
            focus,
            debug
        });

        // Handle 202 Accepted - exercises are being generated
        if (response.status === 202) {
            throw new ApiError(
                response.data?.message || 'Exercises are being generated. Please try again in a moment.',
                202,
                false,
                'exercises_generating',
                response.data
            );
        }

        return response.data.data;
    }

    /**
     * Generate a practice session with specific exercise IDs (debug mode)
     * Used for testing/debugging specific exercises via URL parameter
     */
    async generatePracticeByExerciseIds(exerciseIds: string): Promise<SessionDataDTO> {
        const response = await coreApiClient.axios.post(`/api/greekbrain/practice/generate`, {
            exercise_ids: exerciseIds
        });
        return response.data.data;
    }

    /**
     * Get final skill mastery levels after session completion
     * Used to show mastery progress (before -> after) on session complete screen
     */
    async getSessionMastery(skillKeys: string[]): Promise<{ skill_mastery: SkillMastery[] }> {
        try {
            const response = await coreApiClient.axios.post(`/api/greekbrain/practice/session/complete`, {
                skill_keys: skillKeys
            });
            return response.data.data;
        } catch {
            return { skill_mastery: [] };
        }
    }

    /**
     * Submit a report for a practice exercise
     * Used when users find issues with exercises (wrong answers, translations, etc.)
     */
    async submitReport(payload: PracticeReportPayload): Promise<void> {
        await coreApiClient.axios.post('/api/greekbrain/practice/report', payload);
    }

    /**
     * Generate a writing-curriculum unit session.
     * `unit` accepts the unit_number (the natural URL form, e.g. 2).
     * Throws on lock / not-found via standard ApiError flow.
     */
    async generateWritingUnitSession(unit: number): Promise<SessionDataDTO> {
        const response = await coreApiClient.axios.post(
            `/api/greekbrain/writing/units/${unit}/session`
        );
        return response.data.data;
    }

    /**
     * Generate a writing "Phrases" drill session — curated A2 toolkit phrases
     * surfaced as WRITE_TRANSLATION exercises (type Greek translation from native prompt).
     */
    async generateWritingTypingSession(): Promise<SessionDataDTO> {
        const response = await coreApiClient.axios.get(
            '/api/greekbrain/writing/typing/session'
        );
        return response.data.data;
    }
}

export const practiceApi = new PracticeApi();
